// v4 (V4-H2, spec-v4 5.1, 5.3, 5.4): Home is the current channel's root (/, or /c/:id for another channel). The
// Director's light first on a phone, then the channel's one next step (rolled up from its collections and one-offs),
// its collections, its one-off videos and its library. On desktop the tree is the main pane and the light, the
// review tray and the credits sit in the right column. A channel switcher shows with two or more channels.
import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { collections, rollUpSteps, stepHref, useCurrentChannel, type ChannelTree } from "../collections";
import { ChannelSwitcher } from "../components/ChannelSwitcher";
import { ErrorNote, fmtCredits, greeting, Icon, useLoad } from "../components/bits";
import { Fold } from "../components/Fold";
import { FormatArt } from "../components/FormatArt";
import { useFormats } from "../components/FormatMark";
import { NextStep } from "../components/NextStep";
import { ReviewTray, useReview, useReviewReminder } from "../components/ReviewTray";
import { Shell } from "../components/Shell";
import { Suggestions, TalkStage } from "../components/Talk";
import { TourButton } from "../components/Tour";
import { CollectionTile, NewCollectionSheet, VideoPoster } from "../components/TreeCards";
import { director } from "../director";
import type { FormatId } from "../formats";
import type { NextStepData } from "../nextstep";
import { Link, navigate } from "../router";
import { rememberTitles } from "../session";
import { openNewVideo, useRefresh, useStore } from "../store";

/** The four types the empty Home invites people to try (one per kind of maker). */
const STARTERS: FormatId[] = ["documentary", "cartoon", "short", "ad"];
/** One-offs whose next step Home asks for (the newest ones; the rest load when opened). */
const STEP_ONE_OFFS = 8;

export function useWide(q = "(min-width: 1024px)") {
  const [wide, setWide] = useState(() => (typeof window !== "undefined" && window.matchMedia?.(q).matches) || false);
  useEffect(() => {
    const m = window.matchMedia?.(q);
    if (!m) return;
    const f = () => setWide(m.matches);
    m.addEventListener("change", f);
    return () => m.removeEventListener("change", f);
  }, [q]);
  return wide;
}

/** Each collection's step (GET /collections/:id) and each recent one-off's (GET /projects/:id/next), by id. */
function useSteps(tree: ChannelTree | null) {
  const [steps, setSteps] = useState<Record<string, NextStepData | null>>({});
  const key = tree ? [...tree.collections.map((c) => `${c.id}:${c.updatedAt}`), ...tree.oneOffs.slice(0, STEP_ONE_OFFS).map((v) => `${v.id}:${v.updatedAt}`)].join("|") : "";
  useEffect(() => {
    if (!tree) return;
    let live = true;
    const put = (id: string, s: NextStepData | null) => live && setSteps((m) => ({ ...m, [id]: s }));
    for (const c of tree.collections)
      collections
        .detail(c.id)
        .then((d) => put(c.id, d.nextStep))
        .catch(() => put(c.id, null));
    for (const v of tree.oneOffs.slice(0, STEP_ONE_OFFS))
      collections
        .next(v.id)
        .then((s) => put(v.id, s))
        .catch(() => put(v.id, null));
    return () => {
      live = false;
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return steps;
}

export function HomeScreen({ channelId }: { channelId?: string } = {}) {
  const tick = useRefresh("projects", "collections");
  const me = useStore((s) => s.me);
  const balance = useStore((s) => s.balance);
  const current = useCurrentChannel();
  const channel = channelId ? (me?.channels.find((c) => c.id === channelId) ?? null) : current;
  const { data: tree, error, loading, reload } = useLoad<ChannelTree | null>(() => (channel ? collections.tree(channel.id) : Promise.resolve(null)), [channel?.id, tick]);
  // the review tray reads whole projects (shots, music); it lists every channel's, like v3
  const { data: projects } = useLoad(() => api.projects(), [tick]);
  // 2026-11-04 (Keaton's report): job updates name their video, so the Director can tell them apart
  useEffect(() => {
    if (projects) rememberTitles(projects);
  }, [projects]);
  const review = useReview(projects);
  useReviewReminder(review);
  const steps = useSteps(tree ?? null);
  const wide = useWide();
  const [newCol, setNewCol] = useState(false);
  const [allOneOffs, setAllOneOffs] = useState(false);
  const first = me?.user.name.split(" ")[0];

  const next = useMemo(
    () =>
      tree
        ? rollUpSteps(
            [...tree.collections.map((c) => ({ step: steps[c.id] ?? null })), ...tree.oneOffs.slice(0, STEP_ONE_OFFS).map((v) => ({ step: steps[v.id] ?? null, title: v.title, projectId: v.id }))],
            `Everything on ${tree.channel.name} is done.`,
          )
        : null,
    [tree, steps],
  );
  const empty = !!tree && !tree.collections.length && !tree.oneOffs.length;
  const noChannel = !!me && !me.channels.length;
  const fresh = noChannel || (empty && !projects?.some((p) => p.channelId === channel?.id));

  const onNext = (s: NextStepData) => {
    const to = stepHref(s);
    if (!to) return;
    director.context(`The user tapped the next step "${s.label}" on Home.`);
    navigate(to);
  };

  const stage = (
    <div className="home-stage">
      <p className="home-greet">{greeting(first)}</p>
      <TalkStage />
      <Suggestions items={fresh ? ["What can you make?", "Make a short documentary", "How does this work?"] : ["Make a new video", "Start a series", "What needs me?"]} />
      {/* Wave 3b: the guided tour, for anyone, any time (a new account is offered it on its own) */}
      <TourButton className="home-tour" />
    </div>
  );

  const credits = (
    <section className="panel balance-panel" aria-label="Credits">
      <div>
        <p className="panel-label">Credits</p>
        <p className="balance-num">{fmtCredits(balance)}</p>
        <p className="balance-sub">Every video shows its price before anything is made</p>
      </div>
      <Link to="/credits" className="btn btn-quiet">
        Buy credits
      </Link>
    </section>
  );

  const tray = !!review.length && <ReviewTray items={review} showProject limit={wide ? 4 : 3} onOpen={(i) => navigate(`/p/${i.projectId}`)} />;

  const treeView = (
    <div className="home-tree">
      {channel && (
        <header className="home-channel">
          <ChannelSwitcher current={channel} />
        </header>
      )}
      {error && <ErrorNote text={`Couldn't load your channel. ${error}`} retry={reload} />}

      {next && <NextStep step={next} onAct={onNext} />}

      {!wide && !!review.length && (
        <Fold id="home.review" title="Needs your review" count={review.length} summary="Frames, clips and music waiting for your OK" className="home-review-fold">
          {tray}
        </Fold>
      )}

      {fresh ? (
        <FirstVideo channelId={channel?.id ?? null} channelName={(projects?.length ?? 0) > 0 ? (channel?.name ?? null) : null} onCollection={channel ? () => setNewCol(true) : null} />
      ) : (
        <>
          <section className="home-section" aria-labelledby="cols-h">
            <header className="section-head">
              <h2 id="cols-h">Collections</h2>
              {channel && (
                <button data-action="collection.new" type="button" className="btn btn-quiet btn-small" onClick={() => setNewCol(true)}>
                  <Icon name="plus" size={16} /> New
                </button>
              )}
            </header>
            {loading && !tree ? (
              <div className="ktile-row" aria-label="Loading collections">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="ktile ktile-skeleton" />
                ))}
              </div>
            ) : tree?.collections.length ? (
              <ul className="ktile-row">
                {tree.collections.map((c) => (
                  <li key={c.id}>
                    <CollectionTile c={c} next={steps[c.id]} />
                  </li>
                ))}
              </ul>
            ) : (
              <button data-action="collection.new" type="button" className="ktile-start" onClick={() => setNewCol(true)}>
                <Icon name="stack" size={22} />
                <span>
                  <strong>Start a collection</strong>
                  <span>A series, a show or a product line: set the cast, look and music once for every video in it.</span>
                </span>
              </button>
            )}
          </section>

          <section className="home-section" aria-labelledby="oneoffs-h">
            <header className="section-head">
              <h2 id="oneoffs-h">One-offs</h2>
              <span className="section-acts">
                {(tree?.oneOffs.length ?? 0) > (wide ? 8 : 3) && (
                  <button type="button" className="btn btn-quiet btn-small" onClick={() => setAllOneOffs(!allOneOffs)} aria-expanded={allOneOffs}>
                    {allOneOffs ? "Fewer" : `All ${tree!.oneOffs.length}`}
                  </button>
                )}
                <button type="button" className="btn btn-quiet btn-small" onClick={() => openNewVideo({ channelId: channel?.id ?? null })} data-help="home.new-video">
                  <Icon name="plus" size={16} /> New video
                </button>
              </span>
            </header>
            {loading && !tree ? (
              <div className="poster-row" aria-label="Loading your videos">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="poster poster-skeleton" />
                ))}
              </div>
            ) : tree?.oneOffs.length ? (
              <ul className={allOneOffs ? "poster-grid" : "poster-row"}>
                {(allOneOffs ? tree.oneOffs : tree.oneOffs.slice(0, wide ? 8 : 6)).map((v) => (
                  <li key={v.id}>
                    <VideoPoster v={v} next={steps[v.id]} />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted home-none">Every video here is in a collection. New videos start as one-offs.</p>
            )}
          </section>
        </>
      )}

      {channel && tree && (
        <Fold id="home.library" title="Channel library" count={tree.library.count} summary="Pictures, music and clips for this channel">
          <p className="muted">Everything uploaded or made for {tree.channel.name}: logos, product photos, music and clips.</p>
          <Link to={`/library?scope=${encodeURIComponent(`channel:${channel.id}`)}`} className="btn btn-quiet btn-small">
            Open the library
          </Link>
        </Fold>
      )}
      {noChannel && (
        <Link to="/c/new/settings" className="panel brand-nudge">
          <span>
            <strong>Set up your channel</strong>
            <span>Colours, logo and narrator, so every video looks like yours. Optional.</span>
          </span>
          <Icon name="brand" size={22} />
        </Link>
      )}
    </div>
  );

  return (
    <Shell home>
      {wide ? (
        <div className="home home-v4 is-wide">
          {treeView}
          <aside className="home-side">
            {stage}
            {tray}
            {credits}
          </aside>
        </div>
      ) : (
        <div className="home home-v4">
          {stage}
          {treeView}
        </div>
      )}
      {newCol && channel && <NewCollectionSheet channelId={channel.id} onClose={() => setNewCol(false)} />}
    </Shell>
  );
}

/** The empty state: an invitation to try a type, with the plates of four of them. */
function FirstVideo({ channelId, channelName, onCollection }: { channelId: string | null; channelName: string | null; onCollection: (() => void) | null }) {
  const formats = useFormats();
  const pick = STARTERS.map((id) => formats?.find((f) => f.id === id)).filter((f) => !!f);
  return (
    <div className="first-video">
      <p className="first-video-lede">
        <strong>{channelName ? `Make the first video for ${channelName}.` : "Make your first video."}</strong> Tap the light and say what you have in mind, or pick a type to start from.
      </p>
      <ul className="starter-grid">
        {(pick.length ? pick : STARTERS.map((id) => ({ id, name: "" }))).map((f) => (
          <li key={f.id}>
            <button type="button" className="starter" onClick={() => openNewVideo({ format: f.id, channelId })} disabled={!f.name}>
              <span className="starter-art">
                <FormatArt format={f.id} />
              </span>
              <span className="starter-name">{f.name || " "}</span>
            </button>
          </li>
        ))}
      </ul>
      <span className="first-video-acts">
        <button type="button" className="btn btn-primary" onClick={() => openNewVideo({ channelId })} data-help="home.new-video">
          <Icon name="plus" size={16} /> See every type
        </button>
        {onCollection && (
          <button type="button" className="btn btn-quiet" onClick={onCollection}>
            <Icon name="stack" size={16} /> Start a collection
          </button>
        )}
      </span>
    </div>
  );
}
